// Dispute open + message + admin resolve schemas.
import { z } from 'zod';

import { httpsUrlSchema, idSchema, richTextSchema } from './common.js';

export const disputeCategorySchema = z.enum([
  'safety',
  'misrepresentation',
  'cancellation',
  'harassment',
  'cash_dispute',
  'other',
]);
export type DisputeCategory = z.infer<typeof disputeCategorySchema>;

export const openDisputeInputSchema = z.object({
  exchange_id: idSchema,
  category: disputeCategorySchema,
  description: richTextSchema(4000).refine((s) => s.trim().length >= 20, {
    message: 'Tell us what happened — at least a sentence so we can act on it.',
  }),
  evidence_urls: z.array(httpsUrlSchema).max(8).optional(),
});
export type OpenDisputeInput = z.infer<typeof openDisputeInputSchema>;

export const postDisputeMessageInputSchema = z.object({
  dispute_id: idSchema,
  audience: z.enum(['parties', 'team_only']).default('parties'),
  body: richTextSchema(4000).refine((s) => s.trim().length > 0, {
    message: 'Add a message before sending.',
  }),
  attachments: z
    .array(
      z.object({
        kind: z.enum(['image', 'pdf', 'audio', 'other']),
        url: z.string().url(),
        filename: z.string().min(1).max(280),
        size_bytes: z.number().int().positive().max(10 * 1024 * 1024),
        mime: z.string().min(1).max(120),
      }),
    )
    .max(4)
    .optional(),
});
export type PostDisputeMessageInput = z.infer<typeof postDisputeMessageInputSchema>;

export const acknowledgeDisputeInputSchema = z.object({
  dispute_id: idSchema,
});
export type AcknowledgeDisputeInput = z.infer<typeof acknowledgeDisputeInputSchema>;

/**
 * FUNC-216 — respondent (the other party on the underlying exchange) records
 * they have seen the dispute. Same payload shape as the admin acknowledge —
 * the worker route distinguishes by who is signed in. Wave 4 stream I will
 * mount the route + UI affordance.
 */
export const respondentAcknowledgeDisputeInputSchema = z.object({
  dispute_id: idSchema,
});
export type RespondentAcknowledgeDisputeInput = z.infer<
  typeof respondentAcknowledgeDisputeInputSchema
>;

export const beginDisputeReviewInputSchema = z.object({
  dispute_id: idSchema,
});
export type BeginDisputeReviewInput = z.infer<typeof beginDisputeReviewInputSchema>;

export const resolveDisputeInputSchema = z.object({
  dispute_id: idSchema,
  resolution_outcome: z.enum([
    'warning',
    'account_suspended',
    'account_closed',
    'venue_paused',
    'mediated_no_action',
    'not_actionable',
  ]),
  resolution_summary: richTextSchema(4000).refine((s) => s.trim().length >= 20, {
    message: 'Write a plain-language summary that both parties can read.',
  }),
});
export type ResolveDisputeInput = z.infer<typeof resolveDisputeInputSchema>;

export const closeDisputeInputSchema = z.object({
  dispute_id: idSchema,
});
export type CloseDisputeInput = z.infer<typeof closeDisputeInputSchema>;
